import React, { useState } from 'react';
import { X, Download, Edit, CreditCard, Calendar, Hash, FileText, Printer } from 'lucide-react';
import { Button } from '../ui/Button';
import { formatCurrency, formatDate, downloadPDF } from '../../lib/utils';
import { PrintablePayment } from '../print/PrintablePayment';

interface ViewPaymentModalProps {
  isOpen: boolean;
  onClose: () => void;
  onEdit: () => void;
  payment: any;
}

export const ViewPaymentModal: React.FC<ViewPaymentModalProps> = ({
  isOpen,
  onClose,
  onEdit,
  payment,
}) => {
  const [showPrintView, setShowPrintView] = useState(false);
  const [downloading, setDownloading] = useState(false);

  if (!isOpen || !payment) return null;

  const getMethodColor = (method: string) => {
    switch (method) {
      case 'cash':
        return 'bg-success-100 text-success-700 border-success-200';
      case 'card':
        return 'bg-primary-100 text-primary-700 border-primary-200';
      case 'bank_transfer':
        return 'bg-blue-100 text-blue-700 border-blue-200';
      case 'upi':
        return 'bg-purple-100 text-purple-700 border-purple-200';
      case 'cheque':
        return 'bg-warning-100 text-warning-700 border-warning-200';
      default:
        return 'bg-gray-100 text-gray-700 border-gray-200';
    }
  };

  const handlePrint = () => {
    setShowPrintView(true);
    setTimeout(() => {
      window.print();
      setShowPrintView(false);
    }, 100);
  };

  const handleDownloadReceipt = async () => {
    setDownloading(true);
    try {
      // In a real app, this would generate a PDF receipt using a library
      await downloadPDF(`Payment_Receipt_${payment.id.slice(-8)}`, JSON.stringify(payment));
    } catch (error) {
      console.error('Error downloading receipt:', error);
      alert('Failed to download receipt. Please try again.');
    } finally {
      setDownloading(false);
    }
  };

  return (
    <>
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-xl shadow-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
          <div className="flex items-center justify-between p-6 border-b border-gray-200 bg-gradient-to-r from-success-50 to-success-100">
            <div className="flex items-center space-x-3">
              <div className="w-12 h-12 bg-success-100 rounded-lg flex items-center justify-center">
                <CreditCard className="w-6 h-6 text-success-600" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-gray-900">Payment Details</h2>
                <p className="text-gray-600">Payment #{payment.id.slice(-8)}</p>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <Button variant="ghost" size="sm" icon={Edit} onClick={onEdit}>
                Edit
              </Button>
              <Button variant="ghost" size="sm" icon={Printer} onClick={handlePrint}>
                Print
              </Button>
              <Button 
                variant="ghost" 
                size="sm" 
                icon={Download} 
                onClick={handleDownloadReceipt}
                loading={downloading}
                disabled={downloading}
              >
                Receipt
              </Button>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-600 transition-colors duration-200"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
          </div>

          <div className="p-6 space-y-6">
            {/* Payment Summary */}
            <div className="bg-gradient-to-r from-success-50 to-success-100 p-6 rounded-lg border border-success-200">
              <div className="text-center">
                <p className="text-success-600 text-sm font-medium mb-2">Payment Amount</p>
                <p className="text-4xl font-bold text-success-700">{formatCurrency(Number(payment.amount))}</p>
                <div className="mt-4">
                  <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium border capitalize ${getMethodColor(payment.method)}`}>
                    <CreditCard className="w-4 h-4 mr-2" />
                    {payment.method.replace('_', ' ')}
                  </span>
                </div>
              </div>
            </div>

            {/* Payment Information */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Payment Information</h3>
                <div className="space-y-3">
                  <div>
                    <span className="text-sm text-gray-600">Payment ID:</span>
                    <p className="font-semibold text-gray-900">#{payment.id.slice(-8)}</p>
                  </div>
                  <div>
                    <span className="text-sm text-gray-600">Payment Date:</span>
                    <p className="text-gray-900 flex items-center">
                      <Calendar className="w-4 h-4 mr-2 text-gray-500" />
                      {formatDate(payment.payment_date)}
                    </p>
                  </div>
                  <div>
                    <span className="text-sm text-gray-600">Recorded On:</span>
                    <p className="text-gray-900">{formatDate(payment.created_at)}</p>
                  </div>
                  {payment.reference_id && (
                    <div>
                      <span className="text-sm text-gray-600">Reference ID:</span>
                      <p className="text-gray-900 flex items-center">
                        <Hash className="w-4 h-4 mr-2 text-gray-500" />
                        {payment.reference_id}
                      </p>
                    </div>
                  )}
                </div>
              </div>

              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Invoice Information</h3>
                <div className="space-y-3">
                  <div>
                    <span className="text-sm text-gray-600">Invoice Number:</span>
                    <p className="font-semibold text-gray-900">{payment.invoice?.invoice_number || 'N/A'}</p>
                  </div>
                  <div>
                    <span className="text-sm text-gray-600">Client:</span>
                    <p className="font-semibold text-gray-900">{payment.invoice?.client?.name || 'Unknown'}</p>
                    {payment.invoice?.client?.company && (
                      <p className="text-sm text-gray-500">{payment.invoice.client.company}</p>
                    )}
                  </div>
                  <div>
                    <span className="text-sm text-gray-600">Invoice Total:</span>
                    <p className="text-gray-900">{formatCurrency(Number(payment.invoice?.total || 0))}</p>
                  </div>
                  {payment.invoice?.client?.email && (
                    <div>
                      <span className="text-sm text-gray-600">Email:</span>
                      <p className="text-gray-900">{payment.invoice.client.email}</p>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Notes */}
            {payment.notes && (
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2 flex items-center">
                  <FileText className="w-5 h-5 mr-2" />
                  Notes
                </h3>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <p className="text-gray-700">{payment.notes}</p>
                </div>
              </div>
            )}

            {/* Payment Receipt */}
            <div className="bg-gray-50 p-6 rounded-lg">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Payment Receipt</h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">Payment Method:</span>
                  <span className="font-medium capitalize">{payment.method.replace('_', ' ')}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Amount Paid:</span>
                  <span className="font-bold text-success-600">{formatCurrency(Number(payment.amount))}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Date:</span>
                  <span className="font-medium">{formatDate(payment.payment_date)}</span>
                </div>
                {payment.reference_id && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">Reference:</span>
                    <span className="font-medium">{payment.reference_id}</span>
                  </div>
                )}
                <div className="border-t pt-2 mt-2">
                  <div className="flex justify-between text-base">
                    <span className="font-semibold">Total Paid:</span>
                    <span className="font-bold text-lg">{formatCurrency(Number(payment.amount))}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Printable Version */}
      <PrintablePayment payment={payment} isVisible={showPrintView} />
    </>
  );
};